import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { HINT } from "./HowWeCount";

const dayLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const LightTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-panel border border-panel-border rounded-md px-3 py-2 text-xs shadow-md">
      <div className="text-slate-500">{dayLabel(label)}</div>
      <div className="num font-bold text-ink text-base">{payload[0].value} new</div>
      <div className="text-slate-400">click for details</div>
    </div>
  );
};

export const DailyChart = ({ series, onSelectDay, selectedRange, dark = false }) => {
  const data = series || [];
  const total = data.reduce((s, d) => s + (d.count || 0), 0);
  const max = data.reduce((m, d) => Math.max(m, d.count || 0), 0);
  const last = data.length - 1;
  // fewer than 30 bars = the monthly board view (the chart starts with the month)
  const monthly = data.length > 0 && data.length < 30;
  const title = monthly ? `New per day · since ${dayLabel(data[0].date)}` : "New per day · 30 days";
  const c = dark
    ? { grid: "#263244", tick: "#7C8AA5", label: "#F1F5F9", faded: "#7F1D1D", cursor: "rgba(220,42,39,0.16)" }
    : { grid: "#E2E8F0", tick: "#94A3B8", label: "#0f172a", faded: "#FCA5A5", cursor: "rgba(220,42,39,0.08)" };
  const tick = (value, index) => (index === 0 ? dayLabel(value) : index === last ? "today" : (monthly ? index % 2 === 0 && last - index > 1 : index % 5 === 0) ? String(new Date(`${value}T00:00:00`).getDate()) : "");
  const peakLabel = ({ x, y, width, value }) =>
    value && value === max ? (
      <text x={x + width / 2} y={y - 4} textAnchor="middle" fill={c.label} fontSize={11} fontWeight={700} fontFamily="JetBrains Mono" data-testid="chart-peak-label">
        {value}
      </text>
    ) : null;

  return (
    <section className="panel flex-1 flex flex-col min-h-0 overflow-hidden fade-up" data-testid="30day-bar-chart">
      <div className="flex items-center justify-between gap-2 px-4 pt-3 tv:px-[17px] tv:pt-[14px]">
        <span className="eyebrow whitespace-nowrap truncate min-w-0" title={HINT.chart} data-testid="chart-title">{title}</span>
        <span className="num text-sm tv:text-[17px] font-bold text-ink shrink-0" data-testid="chart-30day-total" title={monthly ? "New venues this month" : "New venues in the last 30 days"}>{total}</span>
      </div>
      <div className="flex-1 min-h-0 h-40 tv:h-auto px-1 pb-1" data-testid="chart-area">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 160 }}>
          <BarChart data={data} margin={{ top: 16, right: 10, left: -24, bottom: 0 }} onClick={(s) => s?.activeLabel && onSelectDay?.(s.activeLabel)} style={{ cursor: onSelectDay ? "pointer" : "default" }}>
            <CartesianGrid vertical={false} stroke={c.grid} />
            <XAxis dataKey="date" tickFormatter={tick} tick={{ fill: c.tick, fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} interval={0} />
            <YAxis allowDecimals={false} tick={{ fill: c.tick, fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
            <Tooltip content={<LightTooltip />} cursor={{ fill: c.cursor }} />
            <Bar dataKey="count" fill="#DC2A27" radius={[3, 3, 0, 0]} isAnimationActive={false} minPointSize={2} className="chart-bar-clickable">
              {data.map((d) => (
                <Cell key={d.date} fill={selectedRange ? (d.date >= selectedRange.from && d.date <= selectedRange.to ? "#DC2A27" : c.faded) : "#DC2A27"} data-testid={`chart-bar-${d.date}`} />
              ))}
              <LabelList dataKey="count" content={peakLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};
